import classnames, { Argument } from 'classnames';
import { extendTailwindMerge } from 'tailwind-merge';

import { isRadiusScaleClass, RADIUS_CLASS_GROUPS } from './radius-scale';
import { isTypographyScaleClass } from './typography-scale';

/** One entry per radius group, so the sided variants are covered as well as the all-corners one. */
const radiusClassGroups = Object.fromEntries(
    RADIUS_CLASS_GROUPS.map((group) => [group, [{ [group]: [isRadiusScaleClass] }]])
);

/**
 * Two families of this workspace's utilities are invisible to tailwind-merge, which knows only the
 * stock scales. Both fail the same silent way -- the conflict is not recognised, both classes survive
 * the merge, and Tailwind's emission order picks the winner -- so both are taught here, which fixes
 * every call at once. The predicates are dependency-free and tested on their own.
 *
 * - `text-*` is ambiguous: `text-body-bold-12` sets a size, `text-gray-dark` sets a colour, and every
 *   unfamiliar `text-…` reads as a colour. So `cn('text-body-bold-12', 'text-gray-dark')` returned
 *   only the colour, which is how a 12px bold pill came out at the inherited 16px regular.
 *   See `typography-scale.ts`.
 * - `rounded-*` is a pixel scale, not t-shirt sizes. Emission order sorts those values as *strings*,
 *   so `cn('rounded-8', 'rounded-12')` resolved to 8px and a caller asking for the wider radius
 *   silently got the narrower one. See `radius-scale.ts`.
 *
 * One stock rule is switched off: tailwind-merge lets a later font size remove an earlier `leading-*`,
 * because stock sizes carry their own line-height. None of this preset's sizes do (they are bare px
 * values), so `cn('leading-24 text-body-14')` kept only the size and the line-height was lost.
 */
const twMerge = extendTailwindMerge({
    extend: {
        classGroups: {
            'font-size': [{ text: [isTypographyScaleClass] }],
            ...radiusClassGroups
        }
    },
    override: {
        conflictingClassGroups: {
            'font-size': []
        }
    }
});

/**
 * Combines `classnames` and `tailwind-merge`:
 * - Accepts the same arguments as `classnames` (strings, arrays, objects, falsy values).
 * - Resolves conflicting Tailwind utility classes deterministically — the later class wins,
 *   without needing `!important` (`!`) overrides.
 *
 * @example
 * cn('justify-center', condition && 'justify-start') // → 'justify-start' wins when condition is true
 * cn('flex p-4', className) // → caller's className overrides defaults cleanly
 */
export const cn = (...args: Argument[]): string => twMerge(classnames(...args));
